import { useRef, useState, useEffect, type Dispatch, type SetStateAction } from "react";
import type { DisappearTimerSec, GroupRecord, MessageEvent } from "@veto/domain";
import { filterExpired } from "@veto/domain";
import {
  getGroupByConversation,
  getMeta,
  listConversations,
  loadDraft,
  upsertConversation,
} from "@veto/storage";

export function useOpenConversation(opts: {
  chatLock: { checkLock: (id: string) => Promise<boolean> };
  listMessages: (id: string) => Promise<MessageEvent[]>;
  setActiveId: (id: string | null) => void;
  setDraft: Dispatch<SetStateAction<string>>;
  setReplyTo: (m: MessageEvent | null) => void;
  setEditing: (m: MessageEvent | null) => void;
  setChatQuery: (q: string) => void;
  setShowDetails: (v: boolean) => void;
  setMessages: (m: MessageEvent[]) => void;
  setActiveGroup: (g: GroupRecord | null) => void;
  setGroupForkWarning: (w: string | null) => void;
  setDisappearTimer: (t: DisappearTimerSec) => void;
  /** How many messages were unread when the chat opened (unread divider + jump). */
  setUnreadAtOpen?: (n: number) => void;
  refresh?: () => Promise<void>;
}): { openConversation: (id: string) => Promise<void> } {
  const seqRef = useRef(0);
  const [chatReadyId, setChatReadyId] = useState<string | null>(null);

  useEffect(() => {
    document.documentElement.dataset.vetoChatReady = chatReadyId ?? "";
  }, [chatReadyId]);

  async function openConversation(id: string) {
    const seq = ++seqRef.current;
    opts.setActiveId(id);
    setChatReadyId(null);
    opts.setDraft("");
    opts.setReplyTo(null);
    opts.setEditing(null);
    opts.setChatQuery("");
    opts.setShowDetails(false);
    const ok = await opts.chatLock.checkLock(id);
    if (seq !== seqRef.current) return;
    if (!ok) {
      opts.setMessages([]);
      return;
    }
    const msgs = filterExpired(await opts.listMessages(id));
    if (seq !== seqRef.current) return;
    const unreadBefore = (await listConversations()).find((c) => c.id === id)?.unread ?? 0;
    if (seq !== seqRef.current) return;
    opts.setUnreadAtOpen?.(unreadBefore);
    opts.setMessages(msgs);
    opts.setActiveGroup((await getGroupByConversation(id)) ?? null);
    if (seq !== seqRef.current) return;
    opts.setGroupForkWarning((await getMeta(`group.fork.${id}`)) ?? null);
    const timerRaw = await getMeta(`disappear.${id}`);
    opts.setDisappearTimer((Number(timerRaw) || 0) as DisappearTimerSec);
    const d = await loadDraft(id);
    if (seq !== seqRef.current) return;
    // Text typed while the chat was loading wins over the saved draft (it used to be wiped).
    opts.setDraft((typed) => typed || (d?.text ?? ""));
    if (d?.replyToEventId) {
      opts.setReplyTo(msgs.find((m) => m.eventId === d.replyToEventId) ?? null);
    }
    // Clear unread when the user opens the conversation (list badge + app badge).
    const conv = (await listConversations()).find((c) => c.id === id);
    if (conv && conv.unread > 0) {
      await upsertConversation({ ...conv, unread: 0 });
      if (seq !== seqRef.current) return;
      await opts.refresh?.();
    }
    if (seq !== seqRef.current) return;
    setChatReadyId(id);
  }

  return { openConversation };
}
